import { Children, cloneElement, ReactElement } from 'react';
import { isAtMostTablet, withBreakpointRange } from '@optimus-web/breakpoints';
import { IconName } from '@optimus-web/icons';
import { DropDownButton } from '../DropDownButton';
import { DropdownItem } from '../DropdownItem';
import { getBreakpointRangeValueFallback } from '../utils/breakpointsFallback';
import { BreadcrumbItem, Wrapper } from './style';
import { BreadcrumbsFactory, BreadcrumbsProps } from './types';

const Separator = () => <div>{'\u002F'}</div>;

export const breadcrumbsFactory = ({ DropdownItemComponent = DropdownItem }: BreadcrumbsFactory = {}) => {
    const BreadcrumbsBase = ({ children, breakpointRange, ...other }: BreadcrumbsProps) => {
        const isSmall = isAtMostTablet(getBreakpointRangeValueFallback(breakpointRange));

        const childrenArray = Children.toArray(children) as Array<ReactElement>;

        const showIndex = isSmall ? -1 : -3;

        let visibleCrumbs = childrenArray.slice(0, -1);
        let hiddenCrumbs = null;
        if (childrenArray.length > 3) {
            visibleCrumbs = childrenArray.slice(showIndex, -1);
            hiddenCrumbs = childrenArray.slice(0, showIndex);
        }

        const activeCrumb = childrenArray.at(-1);

        return (
            <Wrapper spacing="50" small={isSmall} {...other}>
                {hiddenCrumbs && (
                    <>
                        <DropDownButton variant="ghost" size="small" icon={IconName.Breadcrumb}>
                            {Children.map(hiddenCrumbs, ({ props: { children: text, ...props } }) => (
                                <DropdownItemComponent {...props} label={text} />
                            ))}
                        </DropDownButton>
                        <Separator />
                    </>
                )}

                {Children.map(visibleCrumbs, (child, index) => (
                    <>
                        {cloneElement(child, { small: isSmall })}
                        {index + 1 !== visibleCrumbs.length && <Separator />}
                    </>
                ))}

                {childrenArray.length > 1 ? <Separator /> : null}
                <BreadcrumbItem active as="span" small={isSmall}>
                    {activeCrumb?.props.children}
                </BreadcrumbItem>
            </Wrapper>
        );
    };

    return withBreakpointRange(BreadcrumbsBase);
};

export const Breadcrumbs = breadcrumbsFactory();
